import React, { useEffect, useRef, useState } from 'react';
import { WarningFull } from '@fabric-ui/icons';
import { flex, shadowPrimaryBottomMd } from '@fabric-ui/theme';
import { Box, Button, Divider } from '@mui/material';
import { CustomContentProps, useSnackbar } from 'notistack';
import { AvButton } from './AvButton';

export type AvSnackBarMessageProps = {
  title: string;
  subTitle?: string;
  message?: string;
  links?: Array<{ label: string; onClick: () => void }>;
  onClickSkipValidation?: (value: boolean) => void;
  isTest?: boolean;
};

type AvSnackBarMessageComponentProps = Partial<CustomContentProps> & AvSnackBarMessageProps & { id: CustomContentProps['id'] };

export const AvSnackBarMessage = React.forwardRef<HTMLDivElement, AvSnackBarMessageComponentProps>(
  ({ title, subTitle, message, links, onClickSkipValidation, isTest = true, action, id }, ref) => {
    const [showMore, setShowMore] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const messageRef = useRef<HTMLDivElement>(null);
    const { closeSnackbar } = useSnackbar();

    useEffect(() => {
      const element = messageRef.current;
      if (element) {
        setIsOverflowing(element.scrollHeight > element.clientHeight);
      }
    }, [message]);

    const renderedAction = typeof action === 'function' ? action(id) : action;
    const showButtons = !isTest || onClickSkipValidation;

    const handleOkClick = () => {
      if (onClickSkipValidation) {
        onClickSkipValidation(false);
      }
      closeSnackbar(id);
    };
    const handleSkipValidationClick = () => {
      if (onClickSkipValidation) {
        onClickSkipValidation(true);
      }
      closeSnackbar(id);
    };

    return (
      <Box
        ref={ref}
        role="alert"
        sx={{
          backgroundColor: 'var(--color-surface-base-primary)',
          border: '1px solid var(--color-border-status-danger-primary)',
          borderRadius: '8px',
          padding: '16px',
          width: '501px',
          boxShadow: shadowPrimaryBottomMd,
          ...flex.col,
          gap: '8px',
        }}>
        <Box sx={{ ...flex.row, gap: '13px', alignItems: 'flex-start' }}>
          <WarningFull style={{ width: 24, height: 24, flexShrink: 0, color: 'var(--color-content-status-danger-secondary)' }} />

          <Box sx={{ ...flex.col, gap: '4px', flexGrow: 1 }}>
            <Box sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '21px', color: 'var(--color-content-base-primary)' }}>{title}</Box>

            {subTitle && <Box sx={{ fontSize: '14px', lineHeight: '21px', color: 'var(--color-content-base-secondary)' }}>{subTitle}</Box>}

            {message && (
              <Box
                ref={messageRef}
                sx={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: 'var(--color-content-base-secondary)',
                  whiteSpace: 'pre-wrap',
                  maxHeight: showMore ? '200px' : '60px',
                  overflowY: showMore ? 'auto' : 'hidden',
                }}>
                {message}
              </Box>
            )}

            {isOverflowing && (
              <Box
                onClick={() => setShowMore(prev => !prev)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '21px',
                  color: 'var(--color-content-interactive-primary-default)',
                  mt: '4px',
                }}>
                {showMore ? 'Show Less' : 'Show More'}
              </Box>
            )}

            {links && links.length > 0 && (
              <Box sx={{ ...flex.row, gap: '12px', alignItems: 'center', mt: '4px' }}>
                {links.map((link, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider orientation="vertical" flexItem sx={{ height: '16px' }} />}
                    <AvButton variant="primaryText" onClick={link.onClick}>
                      {link.label}
                    </AvButton>
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>

          {renderedAction && <Box sx={{ flexShrink: 0 }}>{renderedAction}</Box>}
        </Box>

        {showButtons && (
          <Box sx={{ ...flex.row, justifyContent: 'flex-end', gap: '8px', pl: '24px' }}>
            {!isTest && (
              <Button variant="outlined" size="xSmall" onClick={handleSkipValidationClick}>
                Skip Validation
              </Button>
            )}
            <Button variant="contained" size="xSmall" onClick={handleOkClick}>
              OK
            </Button>
          </Box>
        )}
      </Box>
    );
  }
);

AvSnackBarMessage.displayName = 'AvSnackBarMessage';

export const useStaticErrorSnackBar = () => {
  const { enqueueSnackbar } = useSnackbar();
  return (params: AvSnackBarMessageProps) => {
    enqueueSnackbar('', {
      variant: 'static' as any,
      persist: true,
      data: params,
    } as any);
  };
};
